/*
 * ap-search-smart.js — zwei Fehlertoleranzen fuer das Suchfeld.
 *
 * 1. GROESSEN-PARSER: Erkennt eine Reifengroesse in jeder ueblichen
 *    Schreibweise ("205/55R16", "205 55 16", "205-55-16", "2055516",
 *    "205/55 r16 91v") und springt beim Abschicken direkt auf die
 *    Groessen-Trefferroute. Die Volltextsuche findet fuer "2055516" NICHTS;
 *    genau diese Eingabe ist bei Reifenkaeufern aber die haeufigste.
 *
 *    ZIEL: zuerst die gemeinsame Zielfunktion window.apSizeTarget
 *    (assets/ap-size-target.js, App-Proxy size-target). Antwortet sie nicht
 *    oder mit null, bleibt es beim bisherigen Ziel — exakt der URL, die auch
 *    das JS-freie Groessenformular baut (snippets/ap-size-search-form.liquid):
 *    /search?q=reifen&options[prefix]=last&filter.p.m.tyre.*=…
 *    Der Resttext der Eingabe geht dabei nicht mehr verloren: eine exakt
 *    erkannte Marke geht als vendor an die Zielfunktion und ersetzt im
 *    Rueckfall das "reifen" als q, ein Saisonwort wird zum Saisonfilter — und
 *    die ganze Eingabe steht auf der Zielseite wieder im Suchfeld.
 *
 *    NUR DIE MARKE geht als q mit, keine sonstigen Restwoerter (Nachpruefung
 *    25.09.2026): Shopify verknuepft die Woerter von q mit UND, jedes muss im
 *    Artikel stehen. Fahrzeug- und Modellnamen des Kunden ("205/55 R16 Golf 7",
 *    "2055516 octavia") stehen in keinem Titel — live mit denselben Filtern:
 *    q=reifen 198 Treffer, q=Golf 4, q=Octavia 1, q=Golf 7 0. Aus "Groesse +
 *    Auto" wurde so eine leere Seite. Dieselbe Regel gilt fuer die Zeile
 *    "Alle Reifen in …" der Schnellsuche: Enter und Klick zeigen dasselbe.
 *
 *    Dieselbe Erkennung setzt in der Schnellsuche die erste Zeile
 *    "Alle Reifen in 205/55 R16 anzeigen" (sections/predictive-search.liquid).
 *
 * 2. MARKEN-KORREKTUR ("blaklion" -> Blacklion): Beim Tippen und auf der
 *    Null-Treffer-Seite wird die Eingabe gegen den Markenindex geprueft
 *    (Levenshtein-Distanz, laengenabhaengige Schwelle). Bei einem nahen,
 *    aber nicht exakten Treffer erscheint "Meinten Sie …?" als echter Link
 *    auf die Markenseite.
 *
 *    Datenquelle ist <script type="application/json" data-ap-brand-index>
 *    (snippets/ap-brand-index.liquid, aus der app-gepflegten Marken-
 *    Navigation). Fehlt der Index, faellt Teil 2 einfach aus — Teil 1
 *    laeuft unabhaengig.
 *
 * 3. CODE-SUCHE (Betreiber-Ja 21.10.2026): ist die GANZE Eingabe eine
 *    Artikelnummer oder eine EAN/GTIN, fuehrt das Absenden ueber die App
 *    (artikel-ziel) direkt auf die Produktseite, sonst auf die Feldsuche
 *    (Nummer: variants.sku:, EAN: variants.barcode: ODER variants.sku:) mit
 *    ausverkauften Treffern am Ende.
 *    Begruendung und Vertrag beim Abschnitt CODE-SUCHE unten.
 *
 * Progressive enhancement: ohne dieses Skript bleibt die Suche eine ganz
 * normale Volltextsuche. Es werden keine Dawn-Dateien veraendert; der
 * Submit-Hook laeuft in der Capture-Phase am Dokument.
 */

(function () {
  'use strict';

  /* ===================================================================== *
   * GROESSEN-PARSER
   * ===================================================================== */

  // Wie parseSize, liefert aber zusaetzlich den Text VOR und NACH der Groesse —
  // in der Originalschreibweise der Eingabe (Marke, Modell, Saisonwort).
  function matchSize(raw) {
    if (!raw) return null;
    var text = String(raw).trim();

    // Kompaktform: genau 7 Ziffern ("2055516"), nichts anderes drumherum.
    var m = text.replace(/\s+/g, '').match(/^(\d{3})(\d{2})(\d{2})$/);
    var before = '';
    var after = '';
    if (!m) {
      // Getrennte Form: 205 [/ . , - Leerzeichen] 55 [r zr / -] 16, optional
      // gefolgt von Last-/Geschwindigkeitsindex ("91v"), Saisonwort etc.
      m = text.match(/(?:^|[^\d])(\d{3})\s*[\/\s.,-]\s*(\d{2})\s*(?:zr|r)?\s*[\/\s.,-]?\s*(\d{2})(?!\d)/i);
      // Kompaktform als eigenes Wort neben Marke/Saison ("michelin 2055516").
      // Nur als ganzes Wort: eine 13-stellige EAN enthaelt keine 7er-Gruppe mit
      // Wortgrenzen, und die Plausibilitaet unten siebt Artikelnummern aus.
      if (!m) m = text.match(/(?:^|\s)(\d{3})(\d{2})(\d{2})(?=\s|$)/);
      if (!m) return null;
      // (?:^|[^\d]) verbraucht ein Zeichen VOR der Breite, wenn es eins gibt —
      // bei "michelin205/55r16" das "n" der Marke. Es gehoert zum Resttext.
      var start = m.index + (/^\d/.test(m[0]) ? 0 : 1);
      before = text.slice(0, start);
      after = text.slice(m.index + m[0].length);
    }

    var width = parseInt(m[1], 10);
    var aspect = parseInt(m[2], 10);
    var rim = parseInt(m[3], 10);

    // Plausibilitaet statt Bestandswissen: dieselben Bereiche wie die
    // statischen Listen des Groessenformulars, plus Rand fuer Exoten.
    if (width < 125 || width > 355) return null;
    if (aspect < 25 || aspect > 85) return null;
    if (rim < 10 || rim > 24) return null;

    return { width: width, aspect: aspect, rim: rim, rest: (before + ' ' + after).trim() };
  }

  function parseSize(raw) {
    var hit = matchSize(raw);
    return hit ? { width: hit.width, aspect: hit.aspect, rim: hit.rim } : null;
  }

  // BISHERIGES Ziel einer reinen Groessensuche — Byte fuer Byte. `q` (die uebrigen
  // Woerter der Eingabe) und `season` kommen nur dazu, wenn die Eingabe mehr als
  // die Groesse trug; ohne beides bleibt die Adresse exakt die alte.
  function sizeUrl(size, q, season) {
    return (
      '/search?q=' + (q ? encodeURIComponent(q) : 'reifen') + '&options%5Bprefix%5D=last' +
      '&filter.p.m.tyre.width=' + size.width +
      '&filter.p.m.tyre.aspect_ratio=' + size.aspect +
      '&filter.p.m.tyre.rim_diameter=' + size.rim +
      (season ? '&filter.p.m.tyre.season=' + encodeURIComponent(season) : '')
    );
  }

  function sizeLabel(size) {
    return size.width + '/' + size.aspect + ' R' + size.rim;
  }

  /* ===================================================================== *
   * ARTIKELNUMMER (Kurz-SKU Stufe 2, Betreiber 29.09.2026): nach dem
   * Umschalten ist die SKU jedes Produkts die kurze Artikelnummer des Shops
   * ("X5P2KG"). Shopify findet sie nur ZUSAMMENGESCHRIEBEN: die Suche zerlegt
   * die Eingabe an Leerzeichen und Bindestrichen und verknuepft die Teile mit
   * UND - live gemessen fand "4250756 103357" nichts, "4250756103357" schon.
   * Kunden diktieren und tippen die Nummer aber gern in Gruppen ("X5P 2KG",
   * "x5p-2kg"). Besteht die GANZE Eingabe nach dem Entfernen der Trenner die
   * Pruefung der App, geht sie zusammengeschrieben und gross an Shopify -
   * beim Absenden (Hook unten) und in der Schnellsuche (getQuery unten). Seit
   * 21.10.2026 als CODE-SUCHE (Abschnitt unten): erst das App-Ziel, sonst die
   * Feldsuche variants.sku:<NR> - die Freitextsuche fand die umgestellten
   * Nummern nur noch zum Teil.
   *
   * Dieselbe Regel wie normalisiereArtikelNummer in der App (tyre-trader-eu,
   * app/lib/catalog/artikel-nummer.ts): 6 Zeichen aus dem Alphabet, vorn ein
   * Buchstabe, Pruefzeichen (Summe i * Index ueber i = 1..6 durch 29
   * teilbar), eine Ziffer in den Stellen 2-5, keine Sperrfolge; erst wenn die
   * direkte Form scheitert, die Verwechsler S -> 5 und U -> V.
   *
   * Bewusst NUR die ganze Eingabe, kein Wortpaar in einem laengeren Satz: eine
   * zufaellige Sechserfolge besteht die Pruefung nur mit 1/29, und jede andere
   * Eingabe (Groesse, Marke, Modell, Freitext; die EAN regelt die CODE-SUCHE)
   * bleibt Byte fuer Byte, wie sie war. Probe: tests/artikelnummer-live.test.js.
   * ===================================================================== */

  var AN_ALPHABET = '23456789ABCDEFGHJKMNPQRTVWXYZ';
  var AN_ANFANG = 'ABCDEFGHJKMNPQRTVWXYZ';
  var AN_SPERRFOLGEN = ['KZ', 'HJ', 'HH', '88', 'KKK', 'NAZ', 'WTF', 'FCK'];
  var AN_VERWECHSLER = { S: '5', U: 'V' };

  function istArtikelnummer(s) {
    if (typeof s !== 'string' || s.length !== 6) return false;
    if (AN_ANFANG.indexOf(s.charAt(0)) < 0) return false;
    var summe = 0;
    for (var i = 0; i < 6; i++) {
      var c = AN_ALPHABET.indexOf(s.charAt(i));
      if (c < 0) return false;
      summe += (i + 1) * c;
    }
    if (summe % 29 !== 0) return false;
    if (!/[2-9]/.test(s.slice(1, 5))) return false;
    for (var j = 0; j < AN_SPERRFOLGEN.length; j++) {
      if (s.indexOf(AN_SPERRFOLGEN[j]) > -1) return false;
    }
    return true;
  }

  // Die gespeicherte Schreibweise oder null (keine Artikelnummer).
  function normalisiereArtikelnummer(eingabe) {
    if (typeof eingabe !== 'string') return null;
    var roh = eingabe.trim().toUpperCase().replace(/[\s._-]+/g, '');
    if (roh.length !== 6) return null;
    if (istArtikelnummer(roh)) return roh;
    var getauscht = roh.replace(/[SU]/g, function (z) {
      return AN_VERWECHSLER[z];
    });
    return getauscht !== roh && istArtikelnummer(getauscht) ? getauscht : null;
  }

  // Der Suchbegriff, der an Shopify geht: die Nummer oder die Eingabe unveraendert.
  function artikelnummerSuchbegriff(eingabe) {
    return normalisiereArtikelnummer(eingabe) || eingabe;
  }

  /* ===================================================================== *
   * CODE-SUCHE: ARTIKELNUMMER ODER EAN (Betreiber-Ja 21.10.2026)
   *
   * MESSBEFUND 21.10.2026 (Ladensuche reifenfair, 1.100 Anfragen): nach dem
   * SKU-Wechsel (Kurzpfad productVariantsBulkUpdate) fand die Freitextsuche
   * rund 57 % der umgestellten, verfuegbaren Produkte weder ueber die Nummer
   * noch ueber die EAN; die Feldsuche q=variants.sku:<NR> fand 33 von 40 dieser
   * Fehltreffer, q=variants.barcode:<EAN> 17 von 21 (stabil). Dazu blendet
   * Search & Discovery AUSVERKAUFTE Produkte aus (ohne Parameter 0 von 88) -
   * erst options[unavailable_products]=last (Suchseite) bzw.
   * resources[options][unavailable_products]=last (Vorhersage) zeigt sie,
   * am Ende der Liste.
   *
   * Ein CODE ist nur die GANZE Eingabe:
   *   - eine Artikelnummer (normalisiereArtikelnummer oben, wie die App), oder
   *   - eine GTIN: nach Entfernen von Leerzeichen, Bindestrichen und Punkten
   *     8, 12, 13 oder 14 Ziffern mit gueltiger Pruefziffer (GS1 Modulo 10 wie
   *     hasValidGtinCheckDigit, tyre-trader-eu app/lib/catalog/gtin.ts) - eine
   *     in Gruppen getippte EAN ("4053949 418302") wird so zusammengezogen.
   * Liest der Groessen-Parser die Eingabe als Reifengroesse, bleibt sie eine
   * Groessensuche ("2055516 0" ergibt als Ziffernfolge eine gueltige EAN-8,
   * gemeint ist 205/55 R16). Beginnt sie in Ziffern wie eine Groesse ausserhalb
   * seiner Bereiche (Agrar, LKW, Zollbreite: "600 65 28 154 151"), ist sie
   * ebenso kein Code (GROESSEN_SCHREIBWEISE unten) und bleibt Freitext.
   * Jede andere Eingabe bleibt Byte fuer Byte.
   *
   * Absenden (Hook unten): erst die App (artikelZiel) - kennt sie GENAU EIN
   * aktives Produkt, geht es direkt auf dessen Seite. Sonst (unbekannt,
   * mehrdeutig, Route noch nicht ausgerollt, Fehler, Zeitlimit) die Suchseite
   * mit der Feldsuche (codeAnfrage: q=variants.sku:<NR> bzw.
   * q=variants.barcode:<GTIN> OR variants.sku:<GTIN>) und
   * options[unavailable_products]=last; die uebrigen Felder des Formulars
   * (options[prefix], die Feldliste aus snippets/ap-suchfelder) gehen mit wie
   * beim normalen Absenden.
   * Schnellsuche: assets/predictive-search.js (getSearchResults) fragt fuer
   * einen Code ebenso die Feldsuche (window.apArtikelnummer.anfrage). Praefix
   * und ODER-Teil sieht der Kunde nie: H1, Suchfelder, Vorschau und
   * Seitentitel zeigen den Code (snippets/ap-suchcode-praefix.liquid).
   * Filter und Sortierung einer Code-Trefferseite behalten die ausverkauften
   * Treffer (snippets/facets.liquid). Probe: tests/suche-nummer-ean.test.js.
   *
   * EAN IN BARCODE ODER SKU (Gegenpruefung 22.10.2026): fuer GTINs aus
   * gesperrten GS1-Baendern und Hausnummern schreibt die App keinen Barcode
   * (barcodeForPush -> null) - ihre EAN steht im Laden NUR in der SKU
   * (reifenfair 3.296, reifen-b2b 1.236 Listungen). variants.barcode: allein
   * fand davon 0 von 5, variants.sku: 5 von 5; nach dem SKU-Wechsel steht die
   * EAN der umgestellten Produkte dagegen nur noch im Barcode (variants.sku:
   * 0 von 2 in der Messung unten). Darum sucht eine GTIN als
   * "variants.barcode:<GTIN> OR variants.sku:<GTIN>". Live gemessen
   * 22.10.2026 (reifenfair): 4 von 4 EANs nur in der SKU und 3 von 3 normale
   * (zwei umgestellte, eine ausverkaufte) je genau 1 Treffer, Suchseite und
   * Vorhersage; eine fremde GTIN 0. "OR" muss GROSS stehen - klein "or"
   * verknuepft wie UND (0 von 4 EANs nur in der SKU); %20, + und Klammern
   * finden dasselbe.
   * ===================================================================== */

  var GTIN_LAENGEN = { 8: true, 12: true, 13: true, 14: true };

  // Pruefziffer nach GS1: Gewichte 3/1 von rechts, ohne die Pruefziffer selbst.
  function gtinPruefzifferStimmt(ziffern) {
    var summe = 0;
    for (var i = ziffern.length - 2, k = 0; i >= 0; i--, k++) {
      summe += Number(ziffern.charAt(i)) * (k % 2 === 0 ? 3 : 1);
    }
    return (10 - (summe % 10)) % 10 === Number(ziffern.charAt(ziffern.length - 1));
  }

  // Die zusammengezogene GTIN oder null (keine GTIN).
  function normalisiereGtin(eingabe) {
    if (typeof eingabe !== 'string') return null;
    var roh = eingabe.replace(/[\s.-]+/g, '');
    if (!/^[0-9]+$/.test(roh) || !GTIN_LAENGEN[roh.length]) return null;
    return gtinPruefzifferStimmt(roh) ? roh : null;
  }

  // Ziffern-SCHREIBWEISE einer Reifengroesse, OHNE die Bereiche von matchSize (Gegenpruefung
  // 22.10.2026). matchSize liest nur Groessen im Fenster des Groessenformulars (Breite 125-355,
  // Querschnitt 25-85, Zoll 10-24); Agrar-, LKW- und Zollbreiten liegen ausserhalb ("600 65 28",
  // "385 65 22.5", "10.0 75 15.3"). Mit ZWEI Lastindizes ergeben ihre Ziffern 13 oder 14 Stellen und
  // in rund 1 von 10 Faellen eine gueltige GS1-Pruefziffer - live 22.10.2026 fand "600 65 28 154 151"
  // im Freitext 36 passende Reifen, als EAN 6006528154151 gelesen 0. Darum ist eine Eingabe, die mit
  // Breite (3 Ziffern oder Zoll mit einer Nachkommastelle) - Querschnitt (2) - Felge (2) beginnt, je
  // durch einen Trenner getrennt, nie ein Code. Keine uebliche EAN-Schreibweise beginnt so (1-6-6, 7-6,
  // Dreier-, Zweier-, Vierergruppen; Probe A in tests/suche-nummer-ean.test.js).
  var GROESSEN_SCHREIBWEISE = /^\s*(?:\d{3}|\d{1,2}[.,]\d)\s*[\s.\/-]\s*\d{2}\s*[\s.\/-]\s*\d{2}(?!\d)/;

  // Der Code einer Eingabe - { art: 'nummer'|'ean', wert, feld } - oder null.
  function suchCode(eingabe) {
    if (typeof eingabe !== 'string' || !eingabe.trim()) return null;
    if (matchSize(eingabe) || GROESSEN_SCHREIBWEISE.test(eingabe)) return null;
    var nummer = normalisiereArtikelnummer(eingabe);
    if (nummer) return { art: 'nummer', wert: nummer, feld: 'variants.sku' };
    var gtin = normalisiereGtin(eingabe);
    if (gtin) return { art: 'ean', wert: gtin, feld: 'variants.barcode' };
    return null;
  }

  // Der Begriff der Feldsuche: "variants.sku:GDA3VA" bzw. fuer eine EAN
  // "variants.barcode:4053949418302 OR variants.sku:4053949418302" (Barcode ODER
  // SKU, Abschnitt oben; "OR" gross). Die Anzeige schneidet Praefix und ODER-Teil
  // ab (snippets/ap-suchcode-praefix.liquid kennt genau diese Form).
  function codeAnfrage(code) {
    var anfrage = code.feld + ':' + code.wert;
    return code.art === 'ean' ? anfrage + ' OR variants.sku:' + code.wert : anfrage;
  }

  // Rueckfall-Ziel: die Suchseite mit der Feldsuche. Die uebrigen Felder des
  // absendenden Formulars gehen mit wie beim normalen Absenden (options[prefix],
  // die Feldliste aus snippets/ap-suchfelder - hier nie eine eigene), dazu
  // ausverkaufte Treffer ans Ende.
  function codeSuchUrl(form, code) {
    var teile = ['q=' + encodeURIComponent(codeAnfrage(code))];
    var daten = null;
    try {
      daten = new window.FormData(form);
    } catch (e) {
      daten = null;
    }
    if (daten && typeof daten.forEach === 'function') {
      daten.forEach(function (wert, name) {
        if (name === 'q' || name === 'options[unavailable_products]' || typeof wert !== 'string') return;
        teile.push(encodeURIComponent(name) + '=' + encodeURIComponent(wert));
      });
    }
    teile.push('options%5Bunavailable_products%5D=last');
    return (form.getAttribute('action') || '/search') + '?' + teile.join('&');
  }

  /*
   * ARTIKEL-ZIEL - Vertrag mit der App (tyre-trader-eu, App-Proxy-Route artikel-ziel):
   *
   *   GET /apps/tyre-search/artikel-ziel?q=<Code, zusammengezogen>
   *   200 {"url": "/products/<handle>", "art": "nummer"|"ean"}  GENAU EIN aktives, gelistetes Produkt
   *   200 {"url": null}                  unbekannt, mehrdeutig, unlesbar, Shop ohne Mandant
   *   200 {"url": null, "retry": true}   Zeitlimit oder Fehler der App - NICHT merken
   *
   * Riegel wie assets/ap-size-target.js, jeder einzeln noetig:
   *   - nur eine shop-eigene, relative Adresse (genau EIN "/" vorn, kein
   *     Rueckstrich, kein Leer- oder Steuerzeichen);
   *   - Zeitlimit 2,5 s wie assets/ap-size-target.js (Gegenpruefung 22.10.2026:
   *     vorher 1,2 s - kuerzer als die eigene Frist der App von 1,5 s, nach der
   *     sie {url:null, retry:true} schickt; ein langsamer Abruf brach dann ab,
   *     bevor die App antworten konnte, und legte die Route 5 Minuten still);
   *     danach die Feldsuche, und die Route ruht 5 Minuten;
   *   - 404 ODER eine Antwort, die kein JSON ist = Route nicht ausgerollt: fuer
   *     den Rest der Sitzung nicht mehr fragen. Gemessen 21.10.2026: vor dem
   *     Ausrollen liefert der App-Proxy fuer die fehlende Route eine ganze
   *     Theme-Seite mit 200 (588 KB, nach ~0,55 s) - als text/html (dann wird
   *     ihr Rest nicht mehr geladen) bzw., weil wir JSON erbitten, mit dem Kopf
   *     application/json: dann entscheidet der Rumpf (beginnt mit "<");
   *   - jeder andere Fehlerstatus (auch 400 = Signatur) und Netzfehler: 5 Minuten Ruhe;
   *   - Antworten (auch null) je Code in sessionStorage, dazu ein Speicher
   *     laufender Abfragen.
   * Die Zusage erfuellt sich IMMER (nie reject): mit der Adresse oder mit null.
   */
  var ZIEL_ENDPUNKT = '/apps/tyre-search/artikel-ziel';
  var ZIEL_FRIST_MS = 2500;
  var ZIEL_RUHE_MS = 5 * 60 * 1000;
  var ZIEL_SPEICHER = 'ap_artikel_ziel:v1:';
  var ZIEL_PAUSE = 'ap_artikel_ziel:pause';
  var zielLaufend = {};

  function speicherLesen(schluessel) {
    try {
      return window.sessionStorage.getItem(schluessel);
    } catch (e) {
      return null;
    }
  }

  function speicherSchreiben(schluessel, wert) {
    try {
      window.sessionStorage.setItem(schluessel, wert);
    } catch (e) {
      /* Speicher aus oder voll - dann eben ohne */
    }
  }

  function zielAdresse(url) {
    if (typeof url !== 'string' || url.charAt(0) !== '/' || url.charAt(1) === '/') return null;
    if (/[\\\s\u0000-\u001f\u007f]/.test(url)) return null;
    return url;
  }

  function zielRuht() {
    var roh = speicherLesen(ZIEL_PAUSE);
    if (!roh) return false;
    if (roh === 'off') return true;
    var bis = Number(roh);
    return isFinite(bis) && bis > Date.now();
  }

  function zielRuhe() {
    if (speicherLesen(ZIEL_PAUSE) !== 'off') speicherSchreiben(ZIEL_PAUSE, String(Date.now() + ZIEL_RUHE_MS));
  }

  function zielAus() {
    speicherSchreiben(ZIEL_PAUSE, 'off');
  }

  // undefined = nichts gemerkt, null = gemerkte Antwort "kein (eindeutiges) Produkt".
  function zielGemerkt(wert) {
    var roh = speicherLesen(ZIEL_SPEICHER + wert);
    if (roh === null) return undefined;
    try {
      var gemerkt = JSON.parse(roh);
      if (gemerkt && typeof gemerkt === 'object' && Object.prototype.hasOwnProperty.call(gemerkt, 'u')) {
        return zielAdresse(gemerkt.u);
      }
    } catch (e) {
      /* fremd beschrieben - neu fragen */
    }
    return undefined;
  }

  function zielAbfrage(wert) {
    return new Promise(function (resolve) {
      var fertig = false;
      var abbruch = typeof AbortController === 'function' ? new AbortController() : null;

      function abbrechen() {
        if (!abbruch) return;
        try {
          abbruch.abort();
        } catch (e) {
          /* schon beendet */
        }
      }

      function ende(url, merken) {
        if (fertig) return;
        fertig = true;
        clearTimeout(uhr);
        if (merken) speicherSchreiben(ZIEL_SPEICHER + wert, JSON.stringify({ u: url }));
        resolve(url);
      }

      var uhr = setTimeout(function () {
        if (fertig) return;
        zielRuhe();
        ende(null, false);
        abbrechen();
      }, ZIEL_FRIST_MS);

      var optionen = { headers: { Accept: 'application/json' }, credentials: 'same-origin' };
      if (abbruch) optionen.signal = abbruch.signal;

      var laufend;
      try {
        laufend = window.fetch(ZIEL_ENDPUNKT + '?q=' + encodeURIComponent(wert), optionen);
      } catch (e) {
        ende(null, false);
        return;
      }

      Promise.resolve(laufend)
        .then(function (antwort) {
          if (fertig) return null;
          if (!antwort) {
            ende(null, false);
            return null;
          }
          if (antwort.status === 404) {
            zielAus();
            ende(null, false);
            return null;
          }
          if (!antwort.ok) {
            zielRuhe();
            ende(null, false);
            return null;
          }
          var typ =
            antwort.headers && typeof antwort.headers.get === 'function'
              ? String(antwort.headers.get('content-type') || '')
              : '';
          if (typ && typ.toLowerCase().indexOf('json') === -1) {
            // Route nicht ausgerollt: der App-Proxy rendert eine Theme-Seite (200 text/html).
            zielAus();
            ende(null, false);
            abbrechen();
            return null;
          }
          return antwort.text().then(function (text) {
            if (fertig) return;
            // Dieselbe Theme-Seite kommt bei "Accept: application/json" mit dem Kopf
            // application/json (gemessen 21.10.2026) - ein Rumpf, der mit "<" beginnt,
            // ist HTML, nie die Antwort der Route: Sitzung aus wie oben.
            if (/^\s*</.test(String(text))) {
              zielAus();
              ende(null, false);
              return;
            }
            var daten = JSON.parse(text); // kaputtes JSON -> catch unten (5 Minuten Ruhe)
            var vorlaeufig = !!(daten && typeof daten === 'object' && daten.retry === true);
            ende(daten && typeof daten === 'object' ? zielAdresse(daten.url) : null, !vorlaeufig);
          });
        })
        .catch(function () {
          if (!fertig) zielRuhe();
          ende(null, false);
        });
    });
  }

  // Ziel fuer einen Code (Objekt aus suchCode oder die Eingabe) -> Promise<url|null>.
  function artikelZiel(code) {
    if (typeof code === 'string') code = suchCode(code);
    if (!code || typeof code.wert !== 'string' || !code.wert) return Promise.resolve(null);
    var wert = code.wert;
    var gemerkt = zielGemerkt(wert);
    if (gemerkt !== undefined) return Promise.resolve(gemerkt);
    if (zielRuht()) return Promise.resolve(null);
    if (zielLaufend[wert]) return zielLaufend[wert];
    var laufend = zielAbfrage(wert).then(function (url) {
      delete zielLaufend[wert];
      return url;
    });
    zielLaufend[wert] = laufend;
    return laufend;
  }

  window.apArtikelnummer = {
    ist: istArtikelnummer,
    normalisiere: normalisiereArtikelnummer,
    suchbegriff: artikelnummerSuchbegriff,
    gtin: normalisiereGtin,
    code: suchCode,
    anfrage: codeAnfrage,
    ziel: artikelZiel,
    zielEndpunkt: ZIEL_ENDPUNKT,
    zielFristMs: ZIEL_FRIST_MS,
  };

  // Schnellsuche: Dawns predictive-search.js holt den Begriff ueber getQuery()
  // (Abruf, Zwischenspeicher, "Suche nach ..."). Die Klasse wird dort erst
  // spaeter definiert (beide Skripte laufen defer, in Dokumentreihenfolge) -
  // deshalb ueber whenDefined, ohne Dawns Datei zu aendern. Einmal je Seite.
  // Ein Code kommt zusammengeschrieben heraus (Nummer gross, EAN ohne Trenner);
  // die Feldsuche daraus baut getSearchResults in predictive-search.js.
  function patchPredictiveSearch() {
    var registry = window.customElements;
    if (!registry || typeof registry.whenDefined !== 'function') return;
    registry.whenDefined('predictive-search').then(function () {
      var Klasse = registry.get('predictive-search');
      var proto = Klasse && Klasse.prototype;
      if (!proto || proto.apArtikelnummerSuche || typeof proto.getQuery !== 'function') return;
      var original = proto.getQuery;
      proto.getQuery = function () {
        var wert = original.call(this);
        var code = suchCode(wert);
        return code ? code.wert : wert;
      };
      proto.apArtikelnummerSuche = true;
    });
  }
  patchPredictiveSearch();

  /* ===================================================================== *
   * RESTTEXT: Marke, Saison, Modell (Befund finden-groessensuche-verwirft-
   * marke-und-modell). Frueher fiel alles ausser der Groesse restlos weg —
   * "Michelin Primacy 4 205/55 R16" zeigte alle Marken, im Suchfeld stand
   * danach "reifen".
   * ===================================================================== */

  // Saisonwoerter werden zum Filterwert der Facette filter.p.m.tyre.season
  // (die App schreibt das DEUTSCHE Label, siehe snippets/tyre-season.liquid).
  var SEASON_WORDS = {
    sommer: 'Sommer',
    sommerreifen: 'Sommer',
    winter: 'Winter',
    winterreifen: 'Winter',
    ganzjahr: 'Ganzjahr',
    ganzjahres: 'Ganzjahr',
    ganzjahresreifen: 'Ganzjahr',
    allwetter: 'Ganzjahr',
    allwetterreifen: 'Ganzjahr',
    allseason: 'Ganzjahr',
  };

  // Fuellwoerter, die als Suchbegriff nichts eingrenzen — oder schlimmer: in
  // keinem Produkttitel stehen und die Volltextsuche auf 0 Treffer druecken
  // ("205/55 R16 guenstig"). "reifen" ist ohnehin der Begriff der Rueckfall-Adresse.
  // Schluessel in brandKey-Form (klein, Umlaute transkribiert, nur a-z0-9).
  var FILLER_WORDS = {
    reifen: 1,
    reifengroesse: 1,
    reifengrosse: 1,
    groesse: 1,
    grosse: 1,
    zoll: 1,
    fuer: 1,
    fur: 1,
    in: 1,
    und: 1,
    mit: 1,
    guenstig: 1,
    gunstig: 1,
    billig: 1,
    preiswert: 1,
    kaufen: 1,
    bestellen: 1,
    angebot: 1,
    angebote: 1,
    preis: 1,
    preise: 1,
    online: 1,
    shop: 1,
    neu: 1,
    neue: 1,
  };

  // Schluessel fuer die Saison- und Fuellwoerter oben: klein, Umlaute VOR dem
  // Sieben transkribiert ("günstig" -> "gunstig"), dann nur a-z0-9. NICHT fuer
  // den Markenindex — der ist seit 24.09.2026 auf normalize() geschluesselt
  // (Umlaute fallen dort weg, siehe loadBrandIndex).
  function brandKey(s) {
    return String(s)
      .toLowerCase()
      .replace(/ä/g, 'a')
      .replace(/ö/g, 'o')
      .replace(/ü/g, 'u')
      .replace(/ß/g, 'ss')
      .replace(/[^a-z0-9]+/g, '');
  }

  function words(text) {
    return String(text || '')
      .split(/[\s,;]+/)
      .map(function (w) {
        return w.replace(/^[^0-9A-Za-zÀ-ÿ]+|[^0-9A-Za-zÀ-ÿ]+$/g, '');
      })
      .filter(Boolean);
  }

  /*
   * Groesse + Resttext -> Suchabsicht:
   *   { size, season, vendor: {n, u}|null, brandWords: [...] }
   * Uebrige Woerter (Modell, Fahrzeug) fallen fuer das Ziel weg — siehe fallbackUrl.
   * Marke nur bei EXAKTEM Treffer im Markenindex (1 bis 3 Woerter am Stueck,
   * laengster zuerst). Keine Unschaerfe: "Conti" soll nicht still bei einer
   * fremden Marke landen — dafuer gibt es das "Meinten Sie …?" beim Tippen.
   */
  function sizeIntent(raw) {
    var hit = matchSize(raw);
    if (!hit) return null;
    var size = { width: hit.width, aspect: hit.aspect, rim: hit.rim };
    var season = '';
    var kept = [];
    words(hit.rest).forEach(function (w) {
      var key = brandKey(w);
      if (SEASON_WORDS[key]) {
        if (!season) season = SEASON_WORDS[key];
        return;
      }
      if (FILLER_WORDS[key]) return;
      kept.push(w);
    });

    var vendor = null;
    var brandWords = [];
    var index = loadBrandIndex();
    for (var len = 3; len >= 1 && !vendor; len--) {
      for (var i = 0; i + len <= kept.length; i++) {
        // Nachschlagen mit normalize() — DEM Schluessel des Markenindex (siehe
        // loadBrandIndex). brandKey() transkribiert Umlaute und bleibt den
        // Saison- und Fuellwoertern vorbehalten; mit ihm fand "König 2055516"
        // die Marke nicht ("konig" gegen den Indexschluessel "knig").
        var key = normalize(kept.slice(i, i + len).join(''));
        if (key.length >= 3 && Object.prototype.hasOwnProperty.call(index, key) && index[key] && index[key].n) {
          vendor = index[key];
          brandWords = kept.slice(i, i + len);
          break;
        }
      }
    }

    return { size: size, season: season, vendor: vendor, brandWords: brandWords };
  }

  // Rueckfall-Ziel (ohne Antwort der Zielfunktion). Als q geht NUR die exakt
  // erkannte Marke mit (intent.brandWords, Begruendung im Dateikopf): sie grenzt
  // den gedeckelten Suchbestand sicher ein, weil sie im Titel jedes ihrer
  // Artikel steht. Jedes andere Restwort — Fahrzeug, Modell, Tippfehler — kann
  // die UND-verknuepfte Volltextsuche auf 0 druecken; ohne Marke bleibt es beim
  // bisherigen "reifen".
  function fallbackUrl(intent) {
    return sizeUrl(intent.size, intent.brandWords.join(' '), intent.season);
  }

  function targetParams(intent) {
    // scope=auto: die Kopfsuche erkennt die METRISCHE PKW-Schreibweise. Fehlt das
    // Blatt in dieser Klasse (Motorradgroesse), antwortet die Route mit null und
    // es bleibt beim Rueckfall — genau wie heute.
    return {
      kind: 'tyre',
      scope: 'auto',
      w: intent.size.width,
      a: intent.size.aspect,
      r: intent.size.rim,
      season: intent.season,
      vendor: intent.vendor ? intent.vendor.n : '',
    };
  }

  // Erst die gemeinsame Zielfunktion (assets/ap-size-target.js), sonst das
  // bisherige Ziel. Ohne das Skript: synchron, wie immer.
  function resolveTarget(intent, fallback, done) {
    var ask = window.apSizeTarget;
    if (typeof ask !== 'function') {
      done(fallback);
      return;
    }
    ask(targetParams(intent)).then(
      function (url) {
        done(url || fallback);
      },
      function () {
        done(fallback);
      }
    );
  }

  /* Die Eingabe bleibt im Feld stehen: vor dem Sprung gemerkt, auf der
     Zielseite in die Suchfelder zurueckgeschrieben — sonst stand dort das
     technische "reifen" der Rueckfall-Adresse. Einmalig, nur fuer genau die
     Adresse, die angesprungen wurde. */
  var INPUT_KEY = 'ap_search_input';

  function samePlace(url) {
    try {
      var a = new URL(url, window.location.origin);
      return a.pathname + a.search === window.location.pathname + window.location.search;
    } catch (e) {
      return false;
    }
  }

  function rememberInput(url, raw) {
    try {
      window.sessionStorage.setItem(INPUT_KEY, JSON.stringify({ url: url, q: raw }));
    } catch (e) {
      /* ohne Speicher steht eben der Suchbegriff der Adresse im Feld */
    }
  }

  function restoreInput() {
    var raw = null;
    try {
      raw = window.sessionStorage.getItem(INPUT_KEY);
    } catch (e) {
      return;
    }
    if (!raw) return;
    var saved = null;
    try {
      saved = JSON.parse(raw);
    } catch (e) {
      saved = null;
    }
    if (!saved || typeof saved.q !== 'string' || typeof saved.url !== 'string') return;
    if (!samePlace(saved.url)) return;
    try {
      window.sessionStorage.removeItem(INPUT_KEY);
    } catch (e) {
      /* egal */
    }
    var inputs = document.querySelectorAll('input[type="search"][name="q"]');
    for (var i = 0; i < inputs.length; i++) inputs[i].value = saved.q;
  }

  /* ===================================================================== *
   * MARKEN-INDEX + LEVENSHTEIN
   * ===================================================================== */

  var brandIndex = null; // { normalisiert: { n: 'Blacklion', u: '/search?…' } }

  // Der Knoten (snippets/ap-brand-index.liquid) traegt seit der Integration der
  // UX-Pakete eine reine NAMENSLISTE: ["Blacklion","König",…]. Schluessel und
  // Suchlink entstehen HIER, mit derselben normalize() wie beim Nachschlagen —
  // die Liquid-Filterkette, die den Schluessel nachbauen musste (und live bei
  // 8 von 725 Marken danebenlag), entfaellt, und der Knoten schrumpft je Seite
  // von rund 64 auf rund 8 KB. Die alte Objektform { schluessel: { n, u } }
  // wird weiter gelesen (gecachte Seiten, Proben).
  function loadBrandIndex() {
    if (brandIndex) return brandIndex;
    brandIndex = {};
    var node = document.querySelector('script[type="application/json"][data-ap-brand-index]');
    if (!node) return brandIndex;
    try {
      var parsed = JSON.parse(node.textContent);
      if (Array.isArray(parsed)) {
        parsed.forEach(function (n) {
          if (typeof n !== 'string') return;
          var k = normalize(n);
          if (k && !Object.prototype.hasOwnProperty.call(brandIndex, k)) {
            brandIndex[k] = { n: n, u: '/search?options%5Bprefix%5D=last&q=' + encodeURIComponent(n) };
          }
        });
      } else {
        brandIndex = parsed || {};
      }
    } catch (e) {
      brandIndex = {};
    }
    return brandIndex;
  }

  function normalize(s) {
    return String(s).toLowerCase().replace(/[^a-z0-9]+/g, '');
  }

  function levenshtein(a, b) {
    if (a === b) return 0;
    var al = a.length;
    var bl = b.length;
    if (!al) return bl;
    if (!bl) return al;
    var prev = new Array(bl + 1);
    var cur = new Array(bl + 1);
    var i;
    var j;
    for (j = 0; j <= bl; j++) prev[j] = j;
    for (i = 1; i <= al; i++) {
      cur[0] = i;
      for (j = 1; j <= bl; j++) {
        var cost = a.charAt(i - 1) === b.charAt(j - 1) ? 0 : 1;
        cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost);
      }
      var tmp = prev;
      prev = cur;
      cur = tmp;
    }
    return prev[bl];
  }

  /*
   * Bester Marken-Kandidat fuer die Eingabe — oder null.
   * Exakte Treffer geben ebenfalls null zurueck: wer richtig tippt, braucht
   * keine Korrektur, die Volltextsuche findet die Marke selbst.
   */
  function suggestBrand(query) {
    var index = loadBrandIndex();
    var token = normalize(query);
    // Kurze Eingaben produzieren nur Falschtreffer ("bf" -> irgendwas).
    if (token.length < 4) return null;
    if (index[token]) return null; // exakt richtig geschrieben

    var best = null;
    var bestDist = 99;
    for (var key in index) {
      if (!Object.prototype.hasOwnProperty.call(index, key)) continue;
      // Schwelle waechst mit der Wortlaenge: 1 Fehler bis 7 Zeichen, sonst 2.
      var limit = key.length >= 8 ? 2 : 1;
      var dist = levenshtein(token, key);
      if (dist > 0 && dist <= limit && dist < bestDist) {
        bestDist = dist;
        best = index[key];
      }
    }
    return best;
  }

  /* ===================================================================== *
   * UI: "Meinten Sie …?" unter dem Suchfeld
   * ===================================================================== */

  function ensureHintNode(form) {
    var hint = form.querySelector('[data-ap-didyoumean-live]');
    if (hint) return hint;
    var field = form.querySelector('.field');
    if (!field) return null;
    hint = document.createElement('div');
    hint.className = 'ap-didyoumean';
    hint.setAttribute('data-ap-didyoumean-live', '');
    hint.hidden = true;
    field.insertAdjacentElement('afterend', hint);
    return hint;
  }

  function renderHint(hint, brand) {
    // Textbausteine kommen aus data-Attributen am Index-Knoten (Locale-gepflegt).
    var node = document.querySelector('script[type="application/json"][data-ap-brand-index]');
    var label = (node && node.dataset.labelDidyoumean) || 'Meinten Sie';
    hint.innerHTML = '';
    var text = document.createElement('span');
    text.textContent = label + ' ';
    var link = document.createElement('a');
    link.href = brand.u;
    link.textContent = brand.n;
    link.className = 'ap-didyoumean__link';
    var mark = document.createElement('span');
    mark.textContent = '?';
    hint.appendChild(text);
    hint.appendChild(link);
    hint.appendChild(mark);
    hint.hidden = false;
  }

  var debounceTimer = null;

  document.addEventListener('input', function (event) {
    var input = event.target;
    if (!input || input.name !== 'q' || input.type !== 'search') return;
    var form = input.form;
    if (!form) return;

    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(function () {
      var hint = ensureHintNode(form);
      if (!hint) return;
      var value = input.value.trim();
      var intent = value ? sizeIntent(value) : null;
      // Erkannte Groesse: das Ziel schon jetzt erfragen (Ergebnis landet im
      // Speicher von ap-size-target.js) — das Absenden wartet dann nicht mehr.
      if (intent) resolveTarget(intent, '', function () {});
      // Bei erkannter Groesse keine Markenkorrektur anbieten.
      var brand = value && !intent ? suggestBrand(value) : null;
      if (brand) {
        renderHint(hint, brand);
      } else {
        hint.hidden = true;
      }
    }, 250);
  });

  /* ===================================================================== *
   * SUBMIT-HOOK: Code erkannt -> App bzw. Feldsuche; Groesse erkannt ->
   * direkt auf die Groessenroute
   * ===================================================================== */

  // Ein in der Schnellsuche per Pfeiltaste gewaehlter TREFFER (ein Link; altes
  // Markup: Link im gewaehlten li, Phase D Q-05: der Link ist die Option) - den
  // oeffnet Dawn (predictive-search.js). "Nach … suchen" ist ein Knopf und
  // zaehlt nicht: wer ihn waehlt, will suchen.
  var GEWAEHLTER_TREFFER = '[aria-selected="true"] a, a[role="option"][aria-selected="true"]';

  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!form || typeof form.getAttribute !== 'function') return;
      var action = form.getAttribute('action') || '';
      if (action.indexOf('/search') === -1) return;
      var input = form.querySelector('input[name="q"]');
      if (!input) return;

      // CODE (Artikelnummer oder EAN als GANZE Eingabe, Abschnitt CODE-SUCHE oben):
      // erst die App, sonst die Feldsuche mit ausverkauften Treffern am Ende. Das
      // Feld zeigt den Code zusammengeschrieben ("X5P 2KG" -> "X5P2KG",
      // "4053949 418302" -> "4053949418302").
      var code = form.querySelector(GEWAEHLTER_TREFFER) ? null : suchCode(input.value);
      if (code) {
        event.preventDefault();
        input.value = code.wert;
        form.setAttribute('aria-busy', 'true');
        var rueckfall = codeSuchUrl(form, code);
        artikelZiel(code).then(function (url) {
          window.location.assign(url || rueckfall);
        });
        return;
      }

      var raw = input.value;
      var intent = sizeIntent(raw);
      if (!intent) return;

      // Hat der Kunde in der Schnellsuche per Pfeiltaste einen Treffer gewaehlt,
      // oeffnet Dawn diesen Treffer (predictive-search.js, selectOption) — dieselbe
      // Bedingung, unter der Dawn den Submit selbst verwirft. Frueher sprang die
      // Seite hier trotzdem auf die Groessenroute, im Wettlauf mit dem Treffer-Link.
      if (form.querySelector('[aria-selected="true"] a')) return;

      event.preventDefault();
      // Wie die Groessensuche selbst: Merker fuer den "Zuletzt gesucht"-Chip
      // schreibt automotive.js beim Formular — hier genuegt die Route.
      form.setAttribute('aria-busy', 'true');
      resolveTarget(intent, fallbackUrl(intent), function (url) {
        rememberInput(url, raw.trim());
        window.location.assign(url);
      });
    },
    true
  );

  // Zurueck-Taste (bfcache): das Formular ist nicht mehr "beschaeftigt".
  window.addEventListener('pageshow', function () {
    var busy = document.querySelectorAll('form[aria-busy="true"]');
    for (var i = 0; i < busy.length; i++) {
      if ((busy[i].getAttribute('action') || '').indexOf('/search') > -1) busy[i].removeAttribute('aria-busy');
    }
  });

  /* ===================================================================== *
   * SCHNELLSUCHE: erste Zeile "Alle Reifen in 205/55 R16 anzeigen"
   * (Befund finden-schnellsuche-ohne-preise-und-groessenzeile). Der Sprung
   * zur Groessenliste stand bisher als "Nach … suchen" UNTER allen zehn
   * Treffern — mobil ausserhalb des sichtbaren Bereichs.
   *
   * Das Markup liefert sections/predictive-search.liquid als verborgenen
   * Platz [data-ap-ps-size] (Texte aus den Locales). Dawn baut die Ergebnisse
   * bei jeder Eingabe per innerHTML neu (predictive-search.js,
   * renderSearchResults) — deshalb ein MutationObserver je Ergebnisbehaelter.
   * ===================================================================== */

  function fillSizeRow(slot) {
    if (!slot || slot.getAttribute('data-ap-ready')) return;
    slot.setAttribute('data-ap-ready', '1');
    var intent = sizeIntent(slot.getAttribute('data-terms') || '');
    if (!intent) return;
    var link = slot.querySelector('[data-ap-ps-size-link]');
    var text = slot.querySelector('[data-ap-ps-size-text]');
    if (!link || !text) return;

    var size = sizeLabel(intent.size) + (intent.season ? ' · ' + intent.season : '');
    var template = intent.vendor
      ? slot.getAttribute('data-label-brand') || ''
      : slot.getAttribute('data-label') || '';
    if (!template) return;
    text.textContent = template
      .split('[size]')
      .join(size)
      .split('[brand]')
      .join(intent.vendor ? intent.vendor.n : '');

    // Die Zeile verspricht ALLE Reifen der Groesse (bzw. der Marke) — dasselbe
    // Ziel wie das Absenden per Enter (fallbackUrl: nur die Marke als q).
    var fallback = fallbackUrl(intent);
    link.setAttribute('href', fallback);
    slot.hidden = false;
    // Vorab fragen: bis zum Klick steht meist schon das bessere Ziel im Link,
    // und das Absenden per Enter findet die Antwort im Speicher.
    resolveTarget(intent, fallback, function (url) {
      link.setAttribute('href', url);
    });
  }

  function watchPredictiveResults() {
    var boxes = document.querySelectorAll('[data-predictive-search]');
    for (var i = 0; i < boxes.length; i++) {
      var box = boxes[i];
      if (box.getAttribute('data-ap-size-watch')) continue;
      box.setAttribute('data-ap-size-watch', '1');
      fillSizeRow(box.querySelector('[data-ap-ps-size]'));
      if (typeof MutationObserver !== 'function') continue;
      new MutationObserver(
        (function (target) {
          return function () {
            fillSizeRow(target.querySelector('[data-ap-ps-size]'));
          };
        })(box)
      ).observe(box, { childList: true });
    }
  }

  /* ===================================================================== *
   * NULL-TREFFER-SEITE: Vorschlag automatisch einsetzen
   * ===================================================================== */

  /*
   * GROESSE AUS DER ADRESSE (Vorschau-Runde 1, 25.09.2026). Marke + Groesse ohne
   * Bestand ("Heidenau 205/55 R16", auch "Golf 7" aus alten Links) fuehrt auf
   * /search?q=Heidenau&filter.p.m.tyre.* mit 0 Treffern. Bei 0 Treffern liefert
   * Shopify search.filters LEER — die Seite wusste serverseitig nichts mehr von
   * der Groesse: H1 "Suchergebnisse", Formular leer ("///"), kein "Alle Reifen
   * in …". Die Groesse steht aber noch in der Adresse. Nur EIN Wert je Feld zaehlt
   * als Groesse (wie ap-search-size-state.liquid); Breite und Zoll sind Pflicht.
   */
  function sizeFromParams(params) {
    var one = function (name) {
      var all = params.getAll(name);
      return all.length === 1 ? String(all[0]).trim() : all.length ? null : '';
    };
    var w = one('filter.p.m.tyre.width');
    var h = one('filter.p.m.tyre.aspect_ratio');
    var d = one('filter.p.m.tyre.rim_diameter');
    var s = one('filter.p.m.tyre.season');
    if (!w || !d || h === null || s === null) return null;
    return { w: w, h: h, d: d, s: s };
  }

  // Exakt die Adresse des Groessenformulars (snippets/ap-size-search-form.liquid):
  // q=reifen statt des Suchworts, das die Liste leer machte; Saison bleibt.
  function paramsSizeUrl(g) {
    return (
      '/search?q=reifen&options%5Bprefix%5D=last' +
      '&filter.p.m.tyre.width=' + encodeURIComponent(g.w) +
      (g.h ? '&filter.p.m.tyre.aspect_ratio=' + encodeURIComponent(g.h) : '') +
      '&filter.p.m.tyre.rim_diameter=' + encodeURIComponent(g.d) +
      (g.s ? '&filter.p.m.tyre.season=' + encodeURIComponent(g.s) : '')
    );
  }

  // Das Groessenformular der Seite mit der Groesse der Adresse vorbelegen — VOR
  // dem Merker (data-preset-mode="first", wie auf der Trefferseite). Kommt der
  // Baum erst noch, liest <tyre-size-search> die Vorgabe beim Eintreffen; stand
  // er schon, wird nachgezogen.
  function presetSizeForms(g) {
    var forms = document.querySelectorAll('tyre-size-search');
    for (var i = 0; i < forms.length; i++) {
      var el = forms[i];
      if (el.hasAttribute('data-preset-w') || el.hasAttribute('data-preset-size')) continue;
      el.setAttribute('data-preset-w', g.w);
      el.setAttribute('data-preset-h', g.h);
      el.setAttribute('data-preset-d', g.d);
      if (g.s) el.setAttribute('data-preset-season', g.s);
      el.setAttribute('data-preset-mode', 'first');
      if (el.tree && typeof el.prefill === 'function') el.prefill();
    }
  }

  function sizeOnlyLabel(g) {
    var label = g.w + (g.h ? '/' + g.h : '') + ' R' + g.d;
    return g.s ? label + ' · ' + g.s : label;
  }

  /*
   * LEERE LISTE TROTZ FILTERN (Vorschau-Runde 2, 25.09.2026): "Trelleborg" hat
   * Produkte, aber keines in 205/55 R16. Dann liefert Shopify search.filters,
   * fuehrt die Groesse aber nicht unter active_values — main-search.liquid kann
   * den Link "Alle Reifen in … anzeigen" nicht selbst setzen und legt nur einen
   * leeren Platz [data-ap-size-slot] hin. Die Groesse steht in der Adresse.
   * Auch nach einem Filterwechsel per AJAX (facets.js tauscht den Behaelter und
   * hat die Adresse dann schon umgeschrieben) — deshalb mit Wurzel aufrufbar.
   */
  function bootSizeOnlySlot(root) {
    var slot = (root || document).querySelector('[data-ap-size-slot]');
    if (!slot || slot.querySelector('a')) return;
    var params = new URLSearchParams(window.location.search);
    if ((params.get('q') || '').trim().toLowerCase() === 'reifen') return;
    var g = sizeFromParams(params);
    if (!g) return;
    var link = document.createElement('a');
    link.href = paramsSizeUrl(g);
    link.className = 'underlined-link link';
    link.setAttribute('data-ap-size-only', '');
    link.textContent = (slot.getAttribute('data-label-all-size') || 'Alle Reifen in [size] anzeigen')
      .split('[size]')
      .join(sizeOnlyLabel(g));
    slot.appendChild(link);
    slot.hidden = false;
    presetSizeForms(g);
  }

  function bootEmptyState() {
    var slot = document.querySelector('[data-ap-didyoumean]');
    if (!slot) return;
    var params = new URLSearchParams(window.location.search);
    var q = (params.get('q') || '').trim();
    if (!q) return;

    var ausAdresse = sizeFromParams(params);
    if (ausAdresse) {
      var label0 = sizeOnlyLabel(ausAdresse);
      var link0 = document.createElement('a');
      link0.href = paramsSizeUrl(ausAdresse);
      link0.className = 'ap-didyoumean__link';
      link0.setAttribute('data-ap-size-only', '');
      link0.textContent = (slot.dataset.labelAllSize || 'Alle Reifen in [size] anzeigen').split('[size]').join(label0);
      slot.appendChild(link0);
      slot.hidden = false;
      presetSizeForms(ausAdresse);
      return;
    }

    var intent = sizeIntent(q);
    if (intent) {
      // Die Volltextsuche hat eine Groesse nicht verstanden — Weiche anbieten.
      var link = document.createElement('a');
      link.href = fallbackUrl(intent);
      link.className = 'ap-didyoumean__link';
      link.textContent = sizeLabel(intent.size);
      var label = document.createElement('span');
      label.textContent = (slot.dataset.labelSize || 'Als Reifengröße suchen:') + ' ';
      slot.appendChild(label);
      slot.appendChild(link);
      slot.hidden = false;
      resolveTarget(intent, link.getAttribute('href'), function (url) {
        link.setAttribute('href', url);
      });
      return;
    }

    var brand = suggestBrand(q);
    if (brand) {
      var label2 = document.createElement('span');
      label2.textContent = (slot.dataset.labelDidyoumean || 'Meinten Sie') + ' ';
      var link2 = document.createElement('a');
      link2.href = brand.u;
      link2.className = 'ap-didyoumean__link';
      link2.textContent = brand.n;
      var mark = document.createElement('span');
      mark.textContent = '?';
      slot.appendChild(label2);
      slot.appendChild(link2);
      slot.appendChild(mark);
      slot.hidden = false;
    }
  }

  /* ===================================================================== *
   * SUCHSEITE: Ueberschrift nach Filterwechsel nachziehen
   * facets.js tauscht beim Filtern per AJAX nur #ProductGridContainer aus.
   * Die Ueberschrift ("205/55 R16 · Sommer – 146 Reifen") stuende danach mit
   * der alten Saison und der alten Zahl da. main-search.liquid legt den
   * passenden Titel deshalb als verborgenen Begleiter IN den Behaelter.
   * ===================================================================== */

  function syncSearchTitle(container) {
    var carrier = container.querySelector('[data-ap-search-title]');
    if (!carrier) return;
    var title = carrier.getAttribute('data-ap-search-title');
    var heading = document.querySelector('[data-ap-search-title-target]');
    if (heading && title && heading.textContent !== title) heading.textContent = title;
    var hits = document.querySelector('[data-ap-search-hits-target]');
    var count = carrier.getAttribute('data-ap-search-hits');
    if (hits && count !== null && hits.textContent !== count) hits.textContent = count;
  }

  function watchSearchTitle() {
    var container = document.getElementById('ProductGridContainer');
    if (!container || !document.querySelector('[data-ap-search-title-target]')) return;
    if (typeof MutationObserver !== 'function') return;
    new MutationObserver(function () {
      syncSearchTitle(container);
      bootSizeOnlySlot(container);
    }).observe(container, { childList: true });
  }

  function boot() {
    restoreInput();
    bootEmptyState();
    bootSizeOnlySlot(document);
    watchPredictiveResults();
    watchSearchTitle();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
